import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Bot,
  Brain,
  Send,
  AlertCircle,
  AlertTriangle,
  Sparkles,
  ExternalLink,
  Square,
  ChevronUp,
  ChevronDown,
  SlidersHorizontal,
} from "lucide-react";
import PersonaAvatar, { TIA_EMOTION_URLS } from "./PersonaAvatar";
import { TiaStageHost, TiaHeadBar, FocusLine, FocusCaption, Chips } from "./tia/TiaChrome";
import type { TiaVariant } from "./tia/TiaChrome";
import type { TiaStageSize } from "./tia/TiaStage";
import { AiExtraItem } from "./ai/AiItems";
import { visibleAiMessages } from '../hooks/aiTurnItems';
import type { AiPersona, PersonaEmotion } from "@roll1/shared";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import AiToolProgress from "./AiToolProgress";
import { TiaMarkdown } from "./ai/TiaMarkdown";
import { AiTurnCardConnected } from "./ai/AiTurnCard";
import { AiUndoConflictHost } from "./ai/AiUndoConflictHost";
import type { AiChange } from "@roll1/shared";
import VoiceInputButton from "./mobile/VoiceInputButton";
import ModelSelect from "./ModelSelect";
import { useAiModels } from "../hooks/useAiModels";
import type { AiChatBase, UseAiChatReturn } from "../hooks/useAiChat";
import type { UsePersonaMemoryReturn } from "../hooks/usePersonaMemory";
import PersonaMemoryPanel from "./PersonaMemoryPanel";
import { useI18n } from "../i18n";
import SidebarPanel from "./ui/SidebarPanel";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import type { AiFocus } from "@roll1/shared";

/** Type guard: does aiChat have the GM's extensions (models, thinking, the undo question)? */
function isGmChat(chat: AiChatBase): chat is UseAiChatReturn {
  return 'undoConflict' in chat;
}

interface AiChatPanelProps {
  aiChat: AiChatBase;
  /** Id сессии — для списка моделей в чате (пакет 3 переезда AI). */
  sessionId?: string;
  onDetach?: () => void;
  isGM?: boolean;
  floating?: boolean;
  /** Current AI persona from sessionState.aiContext.persona (052-ai-persona) */
  persona?: AiPersona | null;
  /** Callback to create timeline events from batch preview (054) */
  /** A link in the change card of an answer: opens the thing (the table: a sheet, a note). */
  onOpenChange?: (change: AiChange) => void;
  canOpenChange?: (change: AiChange) => boolean;
  /** Persona memory hook for GM (056-ai-persona-evolution) */
  personaMemory?: UsePersonaMemoryReturn;
  /** Callback to save chronicle text as a journal handout (056) */
  onSaveChronicle?: (text: string) => void;
  /**
   * The Studio column renders this same panel (owner 31.10: the Studio's own copy had no model choice, no voice, no
   * send / stop): its stage size key, the button that folds the column, the digest in the head bar, its focus, its empty text.
   */
  variant?: TiaVariant;
  stageTrailing?: ReactNode;
  headLeading?: ReactNode;
  focusOverride?: AiFocus;
  emptyText?: string;
}

function splitErrorContent(content: string): {
  clean: string;
  errors: string[];
} {
  const errorRegex = /\[ERROR:[^\]]*\]/g;
  const errors: string[] = [];
  const clean = content.replace(errorRegex, (match) => {
    errors.push(match);
    return "";
  });
  return { clean: clean.trim(), errors };
}

function AiErrorBlock({ errors, isGM }: { errors: string[]; isGM: boolean }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="ai-error-block mt-2 rounded border border-warn/30 bg-warn/10 p-2 text-ui">
      <div className="flex items-center gap-1.5 text-warn">
        <AlertTriangle size={14} />
        <span>{t("ai.toolErrorFriendly")}</span>
        {isGM && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-auto opacity-70 hover:opacity-100"
            type="button"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
      </div>
      {expanded && isGM && (
        <pre className="mt-1 whitespace-pre-wrap text-meta text-muted-foreground">
          {errors.join("\n")}
        </pre>
      )}
    </div>
  );
}

export default function AiChatPanel({
  aiChat,
  sessionId,
  onDetach,
  isGM = true,
  floating,
  persona,
  onOpenChange,
  canOpenChange,
  personaMemory,
  onSaveChronicle,
  variant = "table",
  stageTrailing,
  headLeading,
  focusOverride,
  emptyText,
}: AiChatPanelProps) {
  const studio = variant === "studio";
  const { t, locale } = useI18n();
  // The model choice is not part of the chat (v2 has none): it hides behind «Models» and stays one click away.
  const [showModels, setShowModels] = useState(false);
  const [showMemoryPanel, setShowMemoryPanel] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isStickyRef = useRef(true);
  const inputRef = useRef(aiChat.input);
  inputRef.current = aiChat.input;

  // Header emotion state: streaming → interested, error → concerned, idle → lastEmotion ?? neutral
  const headerEmotion: PersonaEmotion | undefined = (() => {
    if (aiChat.isStreaming) return 'interested';
    if (aiChat.error) return 'concerned';
    return aiChat.lastEmotion ?? 'neutral';
  })();

  // Preload all emotion assets on mount (T013)
  useEffect(() => {
    Object.values(TIA_EMOTION_URLS).forEach(url => {
      const img = new Image();
      img.src = url;
    });
  }, []);

  // Sticky autoscroll logic
  function checkSticky() {
    const el = scrollContainerRef.current;
    if (!el) return;
    isStickyRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
  }

  useEffect(() => {
    if (isStickyRef.current && messagesEndRef.current?.scrollIntoView) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [aiChat.messages]);

  const handleSend = useCallback(() => {
    // The GM may send over a running answer (it is interrupted); a player waits for theirs.
    if (!aiChat.input.trim() || (aiChat.isStreaming && !(isGM && isGmChat(aiChat)))) return;
    aiChat.sendMessage(aiChat.input);
    aiChat.setInput("");
  }, [aiChat, isGM]);

  // Elapsed time indicator during streaming
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    if (!aiChat.isStreaming) {
      setElapsedSec(0);
      return;
    }
    const interval = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [aiChat.isStreaming]);

  // Thinking indicator: show when streaming and last message is user (or no messages)
  const showThinking =
    aiChat.isStreaming &&
    (aiChat.messages.length === 0 ||
      aiChat.messages[aiChat.messages.length - 1].role === "user");

  const gm = isGM && isGmChat(aiChat) ? aiChat : null;
  const undoChat = isGmChat(aiChat) ? aiChat : null;
  // The card of an answer with its undo: the GM's, and a player's own.
  const undoTurn = aiChat.undoTurn;
  const advising = aiChat.setAllowEdits !== undefined && aiChat.allowEdits !== true;
  const focus = focusOverride ?? (isGM ? aiChat.getFocus?.() : undefined);
  const extraHandlers = {
    onApplyPlan: aiChat.applyPlan,
    onDismissPlan: aiChat.dismissPlan,
    onHideResult: aiChat.setResultHidden ? (id: string) => aiChat.setResultHidden?.(id, true) : undefined,
    busy: aiChat.isStreaming,
    onRunConfirmed: aiChat.runConfirmed,
    onPickOption: aiChat.pickOption,
    onCloseTopic: aiChat.closeTopic,
  };
  // Выбор моделей в чате (пакет 3 переезда AI): без id сессии список не грузится и выбор не показывается.
  const aiModels = useAiModels(gm ? sessionId : undefined);

  const aiContent = (
    <>
      {/* Tia's stage: the same one in the Studio column, the table chat and the player's chat (a strip at the table by default) */}
      {(studio || aiChat.stageState !== undefined) && (
        <TiaStageHost chat={aiChat} persona={persona} sizeKey={studio ? "tiaStageSize" : "tiaStageSizeTable"} defaultSize={(studio ? "normal" : "strip") satisfies TiaStageSize} trailing={stageTrailing} />
      )}
      <TiaHeadBar chat={aiChat} leading={headLeading} onOpen={onOpenChange} canOpen={canOpenChange} />

      {/* Persona Memory Panel (056) */}
      {showMemoryPanel && personaMemory && (
        <PersonaMemoryPanel
          memory={personaMemory}
          onClose={() => setShowMemoryPanel(false)}
          onSaveChronicle={onSaveChronicle}
        />
      )}


      {/* Messages */}
      <div
        className="ai-chat-messages group-has-[[data-size=perch]]/col:pt-[46px]"
        ref={scrollContainerRef}
        onScroll={checkSticky}
      >
        {aiChat.messages.length === 0 && !aiChat.isStreaming && emptyText !== undefined && (
          <p className="px-1 text-ui text-dim">{emptyText}</p>
        )}
        {aiChat.messages.length === 0 && !aiChat.isStreaming && emptyText === undefined && (
          <div className="ai-chat-empty">
            {persona === null ? (
              <>
                <Sparkles size={24} className="text-muted-foreground" />
                <p>{t("ai.emptyHint")}</p>
              </>
            ) : (
              <>
                <PersonaAvatar persona={persona} size="lg" />
                <p>
                  {(!persona || (persona.type === 'builtin' && persona.sourceId === 'tia'))
                    ? t("ai.emptyTiaGreeting")
                    : t("ai.emptyPersonaGreeting").replace('{name}', persona.displayName)}
                </p>
              </>
            )}
          </div>
        )}

        {visibleAiMessages(aiChat.messages, aiChat.hiddenResultIds).map((msg) => (
          <div key={msg.id} className={`ai-message ai-message--${msg.role}`}>
            {msg.role === "user" && (
              <div className="ai-message__bubble ai-message__bubble--user !bg-secondary !text-foreground">
                {msg.focus !== undefined && <FocusCaption focus={msg.focus} className="mb-0.5 text-pill text-ai/80" />}
                {msg.focus === undefined && !isGM && <div className="mb-0.5 text-pill text-ai/80">{t("ai.focus.table")}</div>}
                {msg.content}
              </div>
            )}
            {msg.role === "assistant" &&
              (() => {
                const parsed = splitErrorContent(msg.content);
                return (
                  <div className="ai-message__row ai-message__row--assistant flex items-start gap-2">
                    {/* [STYLE_TODO] per-message avatar sizing/spacing */}
                    <PersonaAvatar persona={persona} size="sm" emotion={msg.emotion} className="mt-1 shrink-0" />
                    <div className="ai-message__bubble ai-message__bubble--assistant">
                      {parsed.clean && <TiaMarkdown text={parsed.clean} />}
                      {parsed.errors.length > 0 && (
                        <AiErrorBlock errors={parsed.errors} isGM={isGM} />
                      )}
                    </div>
                  </div>
                );
              })()}
            {/* A player's AI still shows its tool lines; the GM's answer has none (AU-18): its tool calls are in the card. */}
            {msg.role === "tool" && msg.toolData && (
              <AiToolProgress toolData={msg.toolData} isGM={isGM} />
            )}
            {/* AU-5 / AU-18: ONE card per answer of what it changed, with its undo; no line per tool call. */}
            {undoTurn !== undefined && msg.role === "changes" && msg.turnData && (
              <AiTurnCardConnected
                turnId={msg.turnData.turnId}
                changes={msg.turnData.changes}
                untracked={msg.turnData.untracked}
                onUndo={undoTurn}
                onOpen={onOpenChange}
                canOpen={canOpenChange}
                busy={aiChat.isStreaming}
                onHide={aiChat.setResultHidden ? () => aiChat.setResultHidden?.(`turn:${msg.turnData!.turnId}`, true) : undefined}
              />
            )}
            <AiExtraItem msg={msg} handlers={extraHandlers} />
            {msg.role === "notice" && (
              <div className="text-meta italic opacity-70" data-testid="ai-notice">{msg.content}</div>
            )}
            {msg.role === "error" && (
              <div className="ai-message__error">
                <AlertCircle size={14} />
                <span>{msg.content}</span>
              </div>
            )}
          </div>
        ))}


        {showThinking && (
          <div className="ai-thinking" data-testid="ai-thinking">
            <div className="ai-thinking__dots">
              <span />
              <span />
              <span />
            </div>
            <span>{t("ai.thinking")}</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {undoChat && (
        <AiUndoConflictHost conflict={undoChat.undoConflict} messages={undoChat.messages} onResolve={undoChat.resolveUndoConflict} />
      )}

      {/* Examples under the box, by what Tia looks at (the GM's) */}
      {focus !== undefined && (
        <Chips empty={aiChat.messages.length === 0 && !aiChat.isStreaming} messages={aiChat.messages} disabled={aiChat.isStreaming || aiChat.input.trim() !== ''} focus={focus} variant="table" onPick={(text) => { aiChat.sendMessage(text); }} />
      )}

      {/* Input — Copilot-style capsule */}
      <div className={`ai-chat-input-area rounded-lg border ${advising ? "border-ai/50" : "border-input"} bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ring-offset-background mx-1.5 mb-1.5`}>
        {focus !== undefined && <FocusLine focus={focus} />}
        <Textarea
          data-ai-composer
          value={aiChat.input}
          onChange={(e) => aiChat.setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={advising ? t("ai.placeholderAdvise") : t("ai.placeholder")}
          disabled={aiChat.isStreaming && !(isGM && isGmChat(aiChat))}
          className="ai-chat-input bg-transparent !border-0 !ring-0 !ring-offset-0 !outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0"
          rows={advising ? 2 : 1}
        />
        <div className="flex items-center gap-0.5 px-1.5 pb-1.5">
          {gm && (
            // The models in a popover: opening them never adds rows to the composer (owner 31.10).
            <Popover open={showModels} onOpenChange={setShowModels}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-7 w-7 p-0 ${showModels ? "text-foreground" : "text-muted-foreground"} hover:text-foreground`}
                  aria-pressed={showModels}
                  title={t("ai.models")}
                  aria-label={t("ai.models")}
                  data-testid="ai-models-toggle"
                >
                  <SlidersHorizontal size={13} />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" side="top" className="grid w-auto grid-cols-[auto_auto] items-center gap-x-2 gap-y-1.5 border-line p-2">
                <span className="text-meta text-dim">{t("ai.textModel")}</span>
                <ModelSelect
                  models={aiModels.assistant}
                  value={gm.textModel ?? aiModels.campaignDefaults.assistant}
                  onChange={(id) => gm.setTextModel(id)}
                  disabled={aiChat.isStreaming}
                  ariaLabel={t("ai.textModel")}
                  testId="ai-text-model"
                />
                <span className="text-meta text-dim">{t("ai.imageModel")}</span>
                <ModelSelect
                  models={aiModels.illustration}
                  value={gm.imageModel ?? aiModels.campaignDefaults.illustration}
                  onChange={(id) => gm.setImageModel(id)}
                  disabled={aiChat.isStreaming}
                  ariaLabel={t("ai.imageModel")}
                  testId="ai-image-model"
                />
              </PopoverContent>
            </Popover>
          )}
          {isGM && (
            <VoiceInputButton
              onTranscript={(text) => {
                const current = inputRef.current;
                aiChat.setInput(current ? `${current} ${text}` : text);
              }}
              disabled={aiChat.isStreaming}
              lang={locale === "ru" ? "ru-RU" : "en-US"}
            />
          )}
          {gm && personaMemory && (
            <Button
              variant="ghost"
              size="sm"
              className="w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => setShowMemoryPanel(!showMemoryPanel)}
              title={t("ai.personaMemory") ?? "Persona Memory"}
            >
              <Brain size={13} />
            </Button>
          )}
          {onDetach && (
            <Button
              variant="ghost"
              size="sm"
              className="w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={onDetach}
              title={t("session.detachPanel")}
            >
              <ExternalLink size={13} />
            </Button>
          )}
          <div className="ml-auto flex items-center gap-1">
            {aiChat.isStreaming ? (
              <>
                {isGM && elapsedSec > 0 && (
                  <span className="text-label text-muted-foreground tabular-nums">
                    {elapsedSec}s
                  </span>
                )}
                <Button
                  onClick={aiChat.abortGeneration}
                  size="icon"
                  className="w-7 rounded-full"
                  variant="destructive"
                  title={t("ai.stop")}
                  data-testid="ai-chat-stop-btn"
                >
                  <Square size={12} />
                </Button>
              </>
            ) : (
              <Button
                onClick={handleSend}
                disabled={!aiChat.input.trim()}
                size="icon"
                className="w-7 rounded-full"
                data-testid="ai-chat-send-btn"
              >
                <Send size={12} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );

  if (floating) {
    return (
      <div className="group/col relative flex flex-col h-full" data-component="AiChatPanel">
        {aiContent}
      </div>
    );
  }

  // Determine header title based on persona
  const headerTitle = persona === null
    ? t("ai.title")
    : (!persona || (persona.type === 'builtin' && persona.sourceId === 'tia'))
      ? t("ai.title")
      : persona.displayName;

  // [STYLE_TODO] header PersonaAvatar emotion styling
  const isTia = persona === undefined || (persona?.type === 'builtin' && persona?.sourceId === 'tia');
  const headerActions = isTia ? (
    <PersonaAvatar persona={persona} size="sm" emotion={headerEmotion} />
  ) : undefined;

  return (
    <SidebarPanel
      id="ai-chat"
      title={headerTitle}
      icon={Bot}
      collapsible={false}
      flex
      actions={headerActions}
    >
      {aiContent}
    </SidebarPanel>
  );
}
